// src/services/eligibilityService.js
//
// The one call behind "Add Inclusion/Exclusion": read a study's protocol and
// come back with its eligibility criteria, verbatim, with the protocol's own
// sub-headings in place.
//
// Thin by design — the shape of what comes back is defined by
// eligibilityChecklist.js and the prompt is defined server-side in
// functions/eligibilityExtraction.js. Nothing here writes: the criteria go into
// the template only when SectionEditor saves the section a coordinator has
// looked at.

import { auth } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { getActiveTenantId, getActiveSiteId } from "./activeTenant";

/** How long the browser waits before giving up. The function's own ceiling is
 *  300s, so this gives the server room to finish and still ends the wait. */
const TIMEOUT_MS = 240000;

/**
 * Extract the eligibility criteria from a protocol PDF.
 *
 * Authenticated the same way protocolExtractionService.callFn is — the Cloud
 * Function verifies the Firebase ID token and the caller's site membership
 * before spending anything on the AI provider.
 *
 * THROWS on every failure (no protocol text, malformed AI output, nothing
 * found) with the server's own message. The caller is expected to fall back to
 * the blank checklist and say why, rather than leave the coordinator with
 * nothing — see SectionEditor.addEligibility.
 *
 * @param {string} protocolUrl  a download URL for the study's protocol PDF
 * @returns {Promise<{inclusion: Array, exclusion: Array, meta: object}>}
 */
export const extractEligibilityCriteria = async (protocolUrl) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  if (!protocolUrl) throw new Error("This study has no protocol on file.");

  const idToken = await user.getIdToken();

  // `fetch` waits forever by default. This call is made behind a full-screen
  // wait, so "forever" means a coordinator sitting in front of a screen that
  // will never move and has no way out of itself — the one failure this must
  // not have. The bound is generous against the function's own 300s ceiling,
  // and timing out here lands in the caller's catch like any other failure: the
  // blank checklist, and a message saying what happened.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  let resp;
  try {
    resp = await fetch(`${FUNCTIONS_BASE}/extractEligibilityCriteria`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      // tenantId/siteId: the server checks membership against the requesting
      // site, not the deployment's own.
      body: JSON.stringify({
        protocolUrl,
        tenantId: getActiveTenantId(),
        siteId: getActiveSiteId(),
      }),
      signal: controller.signal,
    });
  } catch (e) {
    if (e.name === "AbortError") {
      throw new Error(
        "reading the protocol took too long — it may be very long, or the connection dropped"
      );
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(
      data.error || `Reading the protocol failed (${resp.status}).`
    );
  }
  return {
    inclusion: Array.isArray(data.inclusion) ? data.inclusion : [],
    exclusion: Array.isArray(data.exclusion) ? data.exclusion : [],
    meta: data.meta || {},
  };
};

/**
 * What the coordinator has to be told about HOW this read went, or "".
 *
 * The checklist's dangerous failure is silent: a read that came back short
 * looks exactly like a read that came back complete. Nobody can tell by looking
 * at the page — only by checking it against the protocol. So where the server
 * knows the read may be partial, it has to say so, at the moment the criteria
 * land, and tell the coordinator what to do about it.
 *
 * Returns "" on the ordinary good path, so nothing interrupts the normal case.
 *
 * @param {object} meta  the `meta` block from extractEligibilityCriteria
 */
export const eligibilityReadWarning = (meta = {}) => {
  const notes = [];
  if (meta.truncated) {
    notes.push(
      "This protocol was too long to read in full, so the criteria may be incomplete."
    );
  } else if (meta.narrowed === false) {
    // The locator couldn't find an eligibility section, so the whole document
    // was searched. That still usually works — it is how this ran before the
    // locator existed — but it is the case most likely to come back short.
    notes.push(
      "The eligibility section couldn't be located in this protocol, so the whole document was searched — the criteria may be incomplete."
    );
  }
  // The server checked the lists against the protocol text and found one that
  // doesn't start at the protocol's first criterion. Its own sentences, as sent.
  if (Array.isArray(meta.warnings)) {
    meta.warnings
      .filter((w) => typeof w === "string" && w.trim())
      .forEach((w) => notes.push(w.trim()));
  }
  if (Array.isArray(meta.degradedVendors) && meta.degradedVendors.length) {
    notes.push(
      "The preferred AI model was unavailable and a fallback was used, which is less accurate on long lists of criteria."
    );
  }
  if (!notes.length) return "";
  return `${notes.join(" ")} Please check the checklist against the protocol before approving it.`;
};
